/**
 * Certificates Report
 *
 * Account-wide list of issued certificates, filterable by course and
 * learner. Each row offers a direct download of the rendered certificate
 * image. CSV export covers whatever's currently in view.
 *
 * Data source: `getAccountCertificates(courseId?, userId?)`. The endpoint
 * returns the full slice in one shot (no pagination today), so we keep
 * search + filtering client-side over the loaded list.
 */

import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Certificate, Course, Login } from '../../apis/api-client';
import { certificatesClient } from '../../apis/client-instances';
import { CourseAutocomplete, EmptyState } from '../common';
import UserAutocomplete from '../common/UserAutocomplete';
import { CertificateIcon, DownloadIcon } from '../icons';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '../ui';
import { alert, getFileUrl } from '../../hooks/function';
import { formatDate } from '../../utils/roleHelpers';

// `formatDate` takes a Date — but the API gives us ISO strings. Tiny
// adapter keeps call sites uncluttered.
const fmt = (s: string | Date | null | undefined): string => (s ? formatDate(new Date(s)) : '—');

export default function CertificatesReport() {
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [certs, setCerts] = useState<Certificate[]>([]);

  // Filters — Course/User autocompletes drive the server query; the
  // search box narrows the client-side view (matches student name or
  // course title).
  const [filterCourse, setFilterCourse] = useState<Course | null>(null);
  const [filterUser, setFilterUser] = useState<Login | null>(null);
  const [query, setQuery] = useState('');

  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const result = await certificatesClient.getAccountCertificates(
        filterCourse?.id ?? undefined,
        filterUser?.id ?? undefined,
      );
      setCerts(result || []);
    } catch {
      alert(enqueueSnackbar, 'Failed to load certificates', 'error');
      setCerts([]);
    } finally {
      setLoading(false);
    }
  }, [filterCourse?.id, filterUser?.id, enqueueSnackbar]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Client-side text filter — title or student name. Kept here (rather
  // than pushed to the API) since the dataset is already in memory.
  const visibleCerts = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return certs;
    return certs.filter((c) => {
      const title = (c.courseTitle || '').toLowerCase();
      const name = (c.studentName || '').toLowerCase();
      return title.includes(q) || name.includes(q);
    });
  }, [certs, query]);

  const handleDownload = async (cert: Certificate) => {
    setDownloadingId(cert.id);
    try {
      const url = await getFileUrl(cert.imageFilename);
      if (!url) {
        alert(enqueueSnackbar, 'Certificate file unavailable', 'error');
        return;
      }
      const slug = (cert.courseTitle || 'course').replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase();
      const name = (cert.studentName || 'student').replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase();
      const res = await fetch(url);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `certificate-${slug}-${name}.png`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      alert(enqueueSnackbar, 'Failed to download certificate', 'error');
    } finally {
      setDownloadingId(null);
    }
  };

  // CSV export — captures the filtered view, not the full account list.
  // Matches the convention used by ActivityReport's download button.
  const handleExportCsv = () => {
    if (visibleCerts.length === 0) return;
    const header = ['Student', 'Course', 'Completed', 'Issued'];
    const rows = visibleCerts.map((c) => [
      c.studentName || '',
      c.courseTitle || '',
      c.completedAt ? fmt(c.completedAt) : '',
      c.createdAt ? fmt(c.createdAt) : '',
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `certificates-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <Box>
      {/* Filters */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3, alignItems: { md: 'center' } }}>
        <TextField
          size="small"
          placeholder="Search by student or course…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ flex: 1, minWidth: 220 }}
        />
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <CourseAutocomplete
            value={filterCourse}
            // Autocomplete signature is polymorphic for multi-select; we
            // narrow to single-select here.
            onChange={(v) => setFilterCourse(Array.isArray(v) ? v[0] ?? null : v)}
            placeholder="Filter by course"
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <UserAutocomplete
            value={filterUser}
            onChange={(v) => setFilterUser(Array.isArray(v) ? v[0] ?? null : v)}
            placeholder="Filter by learner"
          />
        </Box>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleExportCsv}
          disabled={visibleCerts.length === 0}
          sx={{ flexShrink: 0 }}
        >
          Export CSV
        </Button>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : visibleCerts.length === 0 ? (
        <EmptyState
          icon={<CertificateIcon sx={{ fontSize: 100, color: 'grey.400' }} />}
          title={certs.length === 0 ? 'No certificates have been issued yet' : 'No matches'}
          subtitle={
            certs.length === 0
              ? 'Certificates appear here once learners complete a course that issues one.'
              : 'Try clearing a filter or adjusting your search.'
          }
        />
      ) : (
        // Plain table layout — small, dense, sortable rows. PagedTable
        // adds pagination ceremony we don't need until the API supports
        // server-side paging on this endpoint.
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '2fr 2fr 1fr 1fr 56px',
              px: 2,
              py: 1.25,
              bgcolor: 'grey.50',
              borderBottom: '1px solid',
              borderColor: 'divider',
              gap: 1,
            }}
          >
            <Typography variant="caption" fontWeight={700} color="text.secondary">STUDENT</Typography>
            <Typography variant="caption" fontWeight={700} color="text.secondary">COURSE</Typography>
            <Typography variant="caption" fontWeight={700} color="text.secondary">COMPLETED</Typography>
            <Typography variant="caption" fontWeight={700} color="text.secondary">ISSUED</Typography>
            <Box />
          </Box>
          {visibleCerts.map((cert) => (
            <Box
              key={cert.id}
              sx={{
                display: 'grid',
                gridTemplateColumns: '2fr 2fr 1fr 1fr 56px',
                px: 2,
                py: 1,
                gap: 1,
                alignItems: 'center',
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:last-of-type': { borderBottom: 0 },
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Typography variant="body2" fontWeight={500} noWrap>
                {cert.studentName || `User #${cert.userId}`}
              </Typography>
              <Typography variant="body2" noWrap>
                {cert.courseTitle || `Course #${cert.courseId}`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {fmt(cert.completedAt)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {fmt(cert.createdAt)}
              </Typography>
              <Tooltip title="Download certificate">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleDownload(cert)}
                    disabled={downloadingId === cert.id}
                  >
                    {downloadingId === cert.id ? <CircularProgress size={18} /> : <DownloadIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
